import { Button } from '@pmi/dsm-react';
import { PrintSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import styles from './cart.module.scss';
import useBreakpoint, { SIZE_SM, SIZE_XS } from '../../hooks/useBreakpoint';
import { toDayMonthYear } from '../../helpers';
import formatCurrency from '../../utils/formatCurrency';
import getCurrencySymbol from '../../utils/getCurrencySymbol';
import { OrderConfirmationData } from './models';

interface OrderConfirmationViewProps {
  accountId?: string;
  data: OrderConfirmationData;
  onGoToOrders?: () => void;
  onReturnToProducts?: () => void;
  showAccessYourItems?: boolean;
  showPrint?: boolean;
}

export default function OrderConfirmationView({
  accountId,
  data,
  onGoToOrders,
  onReturnToProducts,
  showAccessYourItems = true,
  showPrint = true,
}: OrderConfirmationViewProps) {
  const bp = useBreakpoint();
  const isMobile = bp === SIZE_SM || bp === SIZE_XS;

  const { accountName, order, items, user, payment, billingAddress } = data;

  const currencySymbol = getCurrencySymbol(order.currencyCode);

  // user
  const firstName = user?.firstName ?? '';
  const lastName = user?.lastName ?? '';
  const email = user?.email ?? '';

  // Billing address
  const address1 = billingAddress?.address1 ?? '';
  const address2 = billingAddress?.address2 ?? '';
  const city = billingAddress?.city ?? '';
  const state = billingAddress?.state ?? '';
  const postalCode = billingAddress?.postalCode ?? '';
  const country = billingAddress?.country ?? '';

  // Card
  const paymentIcon = `/payment-icons/icon/${
    payment?.ccType && payment?.ccLast4 ? payment.ccType : payment?.method
  }.svg`;

  const paymentLabel =
    payment?.ccType && payment?.ccLast4
      ? `${payment.ccType} ending in ${payment.ccLast4}`
      : payment?.method;

  const handlePrint = () => {
    window.print();
  };

  const handleGoToOrders = () => {
    if (onGoToOrders) {
      onGoToOrders();
    }
  };

  const handleReturnToProducts = () => {
    if (onReturnToProducts) {
      onReturnToProducts();
      return;
    }
    window.location.href = `/hub/${accountId}`;
  };

  const renderPrintAndDownloadButtons = () => (
    <Button
      fullWidth={isMobile}
      className={`print-hidden ${isMobile ? 'mt-3 mb-4' : ''}`}
      icon={PrintSolidIcon}
      iconAlign="left"
      titleText="Print Order"
      enforceTitleCase={false}
      size="md"
      variant="outline-secondary"
      onClick={handlePrint}
    />
  );

  const renderAccessYourItems = () => (
    <div className="print-hidden">
      <div className="typography20 gray800 fw-semibold mb-3">
        Access Your Items
      </div>
      <div className="block d-md-flex gap-5">
        <div className="mb-2">
          <div className="typography18 gray600 mb-3">
            View your products and see what else is available
          </div>
          <Button
            onClick={handleReturnToProducts}
            fullWidth={isMobile}
            enforceTitleCase={false}
            titleText="Return to Products"
            size="md"
            variant="primary"
          />
        </div>

        <div className="mb-2">
          <div className="typography18 gray600 mb-3">
            View your order history
          </div>
          <Button
            onClick={handleGoToOrders}
            fullWidth={isMobile}
            enforceTitleCase={false}
            titleText="Go to Orders"
            size="md"
            variant="outline-secondary"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.orderConfirmationContainer}>
      <div>
        <div className="block d-md-flex justify-content-between align-items-center mb-4">
          <div
            className={`${styles.headerText} typography${
              isMobile ? 24 : 32
            } gray800`}
          >
            Thanks for your order{firstName ? `, ${firstName}` : ''}!
          </div>

          {showPrint && (
            <div className="d-none d-md-flex gap-4">
              {renderPrintAndDownloadButtons()}
            </div>
          )}
        </div>

        <div className="block d-md-flex gap-5 align-items-start">
          <div style={{ flex: 0.6 }}>
            <div className="typography20 gray800 fw-semibold mb-2">
              Order # {order?.incrementId}
            </div>
            <div className="typography18 gray600 mb-4">
              An order confirmation email has been sent to{' '}
              <span className="fw-bold">{email}</span> If you do not receive the
              email, please check your spam/junk folder.
            </div>
            <div className="block d-md-flex gap-4 mt-2 mb-3">
              <div
                className={`${styles.label} typography18 gray600 fw-semibold`}
              >
                Date Ordered:
              </div>
              <div className="typography18 gray600">
                {toDayMonthYear(order?.orderDate)}
              </div>
            </div>
            <div className="block d-md-flex  gap-4 mb-3">
              <div
                className={`${styles.label} typography18 gray600 fw-semibold`}
              >
                Order Total:
              </div>
              <div className="typography18 gray600">
                {currencySymbol}
                {formatCurrency(order.grandTotal)}
              </div>
            </div>
            <div className="block d-md-flex  gap-4 mb-3">
              <div
                className={`${styles.label} typography18 gray600 fw-semibold`}
              >
                Payment Method:
              </div>
              <div className="d-flex gap-2">
                <img alt="CardIcon" src={paymentIcon} />
                <span className="typography18 gray600">{paymentLabel}</span>
              </div>
            </div>
            <div className="block d-md-flex  gap-4 mb-3">
              <div
                className={`${styles.label} typography18 gray600 fw-semibold`}
              >
                Account Name:
              </div>
              <div className="typography18 gray600">{accountName}</div>
            </div>
            <div className="block d-md-flex  gap-4 mb-3">
              <div
                className={`${styles.label} typography18 gray600 fw-semibold`}
              >
                Account ID:
              </div>
              <div className="typography18 gray600">{accountId}</div>
            </div>
            {(firstName || lastName) && (
              <div className="block d-md-flex gap-4 mb-3">
                <div
                  className={`${styles.label} typography18 gray600 fw-semibold`}
                >
                  Ordered by:
                </div>
                <div className="typography18 gray600">
                  {firstName} {lastName}
                </div>
              </div>
            )}
            <div className="block d-md-flex  gap-4 mb-3">
              <div
                className={`${styles.label} typography18 gray600 fw-semibold`}
              >
                Billing Address:
              </div>
              <div className="typography18 gray600">
                {address1} {address2 && `${address2} `}
                {city}, {state} {postalCode} {country}
              </div>
            </div>
            <div className={`${styles.divider} my-4`} />
            {showAccessYourItems && (
              <div style={{ flex: 0.6 }} className="d-none d-md-block">
                {renderAccessYourItems()}
              </div>
            )}
          </div>
          <div style={{ flex: 0.4 }}>
            <div className={`${styles.cardContainer} mb-4`}>
              <div className="typography20 gray800 fw-semibold">
                Order Receipt
              </div>
              <div className={`${styles.divider} my-3`} />
              <div>
                <div className="typography16 gray600 mb-3">
                  {items.length} Item{items.length > 1 ? 's' : ''} ordered
                </div>
                {items.map((item) => {
                  const chapterName = item?.chapterName
                    ? `Including ${item?.chapterName}`
                    : '';
                  return (
                    <div
                      key={item.id}
                      className="d-flex justify-content-between gap-4 mb-3"
                    >
                      <div>
                        <div className="typography16 gray800 fw-semibold">
                          {item.name} {chapterName}
                        </div>
                      </div>
                      <div className="typography18 gray800">
                        {currencySymbol}
                        {formatCurrency(item.subtotal)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={`${styles.divider} my-3`} />
              <div className="d-flex justify-content-between gap-4 mb-2">
                <div className="typography16 gray700">Subtotal</div>
                <div className="typography16 gray700 text-end">
                  {currencySymbol}
                  {formatCurrency(order.subtotal)}
                </div>
              </div>
              {order?.discountTotal > 0 && (
                <div className="d-flex justify-content-between gap-4 mb-2">
                  <div className="typography16 gray800">
                    {accountName} Discount
                  </div>
                  <div
                    className={`typography16 gray600 text-end ${styles.discountPriceText}`}
                  >
                    -{currencySymbol}
                    {formatCurrency(order?.discountTotal)}
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-between gap-4 mb-2">
                <div className="typography16 gray700">Tax</div>
                <div className="typography16 gray700 text-end">
                  {currencySymbol}
                  {formatCurrency(order?.taxTotal)}
                </div>
              </div>
              <div className={`${styles.divider} my-2`} />
              <div className="d-flex justify-content-between gap-4">
                <div className="typography18 gray900 fw-bold">Order Total</div>
                <div className="typography20 gray900 fw-bold text-end">
                  {currencySymbol}
                  {formatCurrency(order?.grandTotal)}
                </div>
              </div>
            </div>

            {showPrint && (
              <div className="block d-md-none">
                {renderPrintAndDownloadButtons()}
              </div>
            )}

            {showAccessYourItems && (
              <div style={{ flex: 0.6 }} className="d-block d-md-none">
                {renderAccessYourItems()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

OrderConfirmationView.defaultProps = {
  accountId: undefined,
  onGoToOrders: undefined,
  onReturnToProducts: undefined,
  showAccessYourItems: true,
  showPrint: true,
};
